import apiService from './apiService';
import { GET, POST, PUT, PATCH, DELETE } from './executer';

/**
 * Circuit entity from the database
 */
export interface Circuit {
  circuit_id: string;
  display_name: string;
  country: string;
  time_zone: string;
  location_name: string;
  latitude?: number;
  longitude?: number;
  updated_by?: string | null;
  update_datetime?: string | null;
  deleted?: boolean;
}

/**
 * Request payload for creating a new circuit
 */
export interface CreateCircuitRequest {
  circuit_id: string;
  display_name: string;
  country: string;
  time_zone: string;
  location_name: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Request payload for updating a circuit
 */
export interface UpdateCircuitRequest {
  display_name?: string;
  country?: string;
  time_zone?: string;
  location_name?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * GeoJSON types
 */
export interface GeoJSONGeometry {
  type: string;
  coordinates: number[] | number[][] | number[][][];
}

export interface GeoJSONFeature {
  type: string;
  geometry: GeoJSONGeometry;
  properties?: Record<string, unknown>;
}

export interface GeoJSONFeatureCollection {
  type: string;
  features: GeoJSONFeature[];
}

/**
 * Circuit with GeoJSON data
 */
export interface CircuitWithGeoJSON extends Circuit {
  geojson: GeoJSONFeatureCollection;
}

/**
 * Response from the circuits API
 */
export interface CircuitsResponse {
  success: boolean;
  data: Circuit[];
  count: number;
}

export interface CircuitResponse {
  success: boolean;
  data: CircuitWithGeoJSON;
}

class CircuitsService {
  /**
   * Get all circuits
   * @param includeDeleted - If true, includes soft-deleted circuits (for management UI)
   */
  async getAllCircuits(includeDeleted: boolean = false): Promise<Circuit[]> {
    try {
      const url = includeDeleted ? '/api/circuits?include_deleted=true' : '/api/circuits';
      const response = await apiService.client.execute<Circuit[]>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch circuits');
    } catch (error) {
      console.error('Error fetching circuits:', error);
      throw error;
    }
  }

  /**
   * Get a circuit by ID with its GeoJSON data from static files
   * @param circuitId - The circuit ID (used for metadata)
   * @param geojsonId - Optional custom GeoJSON file ID (defaults to circuitId if not provided)
   */
  async getCircuitById(circuitId: string, geojsonId?: string | null): Promise<CircuitWithGeoJSON> {
    // Use geojsonId if provided, otherwise fall back to circuitId
    const fileId = geojsonId || circuitId;
    
    try {
      // Fetch the GeoJSON from the public/circuits folder
      // Use cache: 'reload' to bypass cache and ensure we get the latest version
      // This is important when GeoJSON files are updated during development
      const response = await fetch(`/circuits/${fileId}.geojson`, {
        cache: 'reload',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch circuit GeoJSON: ${fileId}`);
      }
      
      const geojson = await response.json() as GeoJSONFeatureCollection;
      
      // Return a minimal circuit object with the GeoJSON
      // The actual circuit metadata comes from the events API
      return {
        circuit_id: circuitId,
        display_name: '',
        country: '',
        time_zone: '',
        location_name: '',
        geojson,
      };
    } catch (error) {
      console.error(`Error fetching circuit ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new circuit
   * Requires: root role
   */
  async createCircuit(circuitData: CreateCircuitRequest, userToken: string) {
    return await apiService.client.execute<Circuit>(
      POST,
      '/api/circuits',
      {
        data: circuitData,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Update an existing circuit
   * Requires: root role
   */
  async updateCircuit(
    circuitId: string,
    updates: UpdateCircuitRequest,
    userToken: string
  ) {
    return await apiService.client.execute<Circuit>(
      PUT,
      `/api/circuits/${circuitId}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Soft-delete a circuit
   * Requires: root role
   */
  async deleteCircuit(circuitId: string, userToken: string) {
    return await apiService.client.execute<{ message: string }>(
      DELETE,
      `/api/circuits/${circuitId}`,
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Set the deleted flag on a circuit (soft-delete or restore)
   * Requires: root role
   */
  async setCircuitDeleted(circuitId: string, deleted: boolean, userToken: string) {
    return await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/circuits/${circuitId}/deleted`,
      {
        data: { deleted },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }
}

export const circuitsService = new CircuitsService();
export default circuitsService;

